/**
 * ErrorBoundary — 컴포넌트 에러 격리
 *
 * 목적: 하위 컴포넌트에서 렌더링 에러 발생 시 전체 흰 화면 대신
 *       사용자 친화적 에러 화면을 표시하고 새로고침 유도
 *
 * 왜 Class 컴포넌트인가:
 *   React의 componentDidCatch / getDerivedStateFromError는
 *   아직 함수형 컴포넌트에서 지원되지 않음 (2025년 기준)
 *   → ErrorBoundary는 반드시 Class 컴포넌트로 작성해야 함
 *
 * 사용법:
 *   <ErrorBoundary>
 *     <SomePage />
 *   </ErrorBoundary>
 */

import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /**
   * 에러 발생 시 state 업데이트 (렌더 단계)
   * getDerivedStateFromError: 에러를 받아 state를 반환하는 정적 메서드
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /**
   * 에러 발생 후 처리 (커밋 단계)
   * componentDidCatch: 로깅, 외부 에러 트래킹 서비스 연동에 사용
   */
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            maxWidth: 480,
            textAlign: 'center',
            border: '1px solid #ffcdd2',
            borderRadius: '12px',
            backgroundColor: '#fff8f8',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 56, color: '#e53935', mb: 2 }} />

          <Typography variant="h6" fontWeight={700} gutterBottom>
            페이지를 불러오지 못했습니다
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            일시적인 오류가 발생했습니다.
            <br />
            새로고침 후에도 반복되면 관리자에게 문의해주세요.
          </Typography>

          {/* 개발 환경에서만 에러 메시지 표시 */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box
              sx={{
                mb: 3, p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textAlign: 'left',
              }}
            >
              <Typography variant="caption" fontFamily="monospace" color="error">
                {this.state.error.toString()}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: '#1976d2' }}
          >
            새로고침
          </Button>
        </Paper>
      </Box>
    );
  }
}
